/**
 * @fileoverview Products workspace shell with tab navigation.
 * @module features/products/pages/ProductsPage
 */

import { ReactElement, useCallback, useState, useEffect, useDeferredValue } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Package, ChevronLeft, ChevronRight, Search, Tag } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import PageMeta from '@/shared/components/common/PageMeta'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollReveal } from '@/shared/components/ui'
import { useIsMobile, useIsBelowDesktop } from '@/shared/hooks/useIsMobile'
import { CatalogTab } from '@/features/products/components/CatalogTab'
import { NeedsReviewTab } from '@/features/sku-alias/pages/NeedsReviewTab'
import { useNeedsReviewCountQuery } from '@/features/sku-alias/api/needsReview'
import { useUnresolvedUrlsCountQuery } from '@/features/sku-alias/api/stagingExternalUrls'
import { getProducts } from '@/lib/productService'
import {
  FloatingActionPanelRoot,
  FloatingActionPanelTrigger,
  FloatingActionPanelContent,
  FloatingActionPanelButton,
} from '@/components/ui/floating-action-panel'

const TAB_PARAM = 'view'

type ProductsTabKey = 'catalog' | 'needsReview'

const tabOrder: ProductsTabKey[] = ['catalog', 'needsReview']

/**
 * Unified Products workspace page.
 *
 * @component
 */
export function ProductsPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const deferredSearch = useDeferredValue(searchInput)
  const isMobile = useIsMobile()
  const isBelowDesktop = useIsBelowDesktop()

  const urlValue = searchParams.get(TAB_PARAM)
  const activeTab: ProductsTabKey = tabOrder.includes(urlValue as ProductsTabKey)
    ? (urlValue as ProductsTabKey)
    : 'catalog'

  const { data: skuAliasCountRaw, isSuccess: skuAliasCountLoaded } = useNeedsReviewCountQuery()
  const { data: urlCountRaw, isSuccess: urlCountLoaded } = useUnresolvedUrlsCountQuery()
  const skuAliasCount = skuAliasCountRaw ?? 0
  const urlCount = urlCountRaw ?? 0
  const needsReviewCount = skuAliasCount + urlCount
  const hasConfirmedNoReviewItems = skuAliasCountLoaded && urlCountLoaded && needsReviewCount === 0

  // Handle search input change - reset page immediately when user types
  const handleSearchChange = (value: string): void => {
    if (value !== searchInput) {
      setPage(0)
    }
    setSearchInput(value)
  }

  const catalogQuery = useQuery({
    queryKey: ['products', { page, size: 10, sort: 'revenue', query: deferredSearch }],
    queryFn: () => getProducts({
      page,
      size: 10,
      sort: 'revenue',
      ...(deferredSearch ? { query: deferredSearch } : {}),
    }),
    placeholderData: (prev) => prev,
  })

  // Redirect to catalog if on needsReview tab but nothing to review
  useEffect(() => {
    if (hasConfirmedNoReviewItems && activeTab === 'needsReview') {
      const updated = new URLSearchParams(searchParams)
      updated.delete(TAB_PARAM)
      setSearchParams(updated, { replace: true })
    }
  }, [hasConfirmedNoReviewItems, activeTab, searchParams, setSearchParams])

  // Auto-adjust page when query returns empty results on non-zero page
  // This is a legitimate sync with external data (query results)
  useEffect(() => {
    if (!catalogQuery.isLoading && !catalogQuery.isPlaceholderData && page > 0 && catalogQuery.data?.content?.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Legitimate sync: correcting page when server returns empty results
      setPage(p => Math.max(0, p - 1))
    }
  }, [catalogQuery.isLoading, catalogQuery.isPlaceholderData, catalogQuery.data?.content?.length, page])

  // Keyboard navigation for pagination
  useEffect(() => {
    if (activeTab !== 'catalog') return

    const handleKeyDown = (e: KeyboardEvent): void => {
      // Skip if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      if (e.key === 'ArrowLeft' && page > 0 && !catalogQuery.isLoading) {
        setPage(p => Math.max(0, p - 1))
      } else if (
        e.key === 'ArrowRight' &&
        !catalogQuery.data?.last &&
        !catalogQuery.isLoading &&
        !catalogQuery.isPlaceholderData &&
        catalogQuery.data?.content?.length &&
        (catalogQuery.data?.totalPages === undefined || page < catalogQuery.data.totalPages - 1)
      ) {
        setPage(p => p + 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTab, page, catalogQuery.isLoading, catalogQuery.isPlaceholderData, catalogQuery.data])

  const setActiveTab = useCallback(
    (next: ProductsTabKey): void => {
      if (next === activeTab) return
      const updated = new URLSearchParams(searchParams)
      if (next === 'catalog') {
        updated.delete(TAB_PARAM)
      } else {
        updated.set(TAB_PARAM, next)
      }
      setSearchParams(updated, { replace: true })
    },
    [activeTab, searchParams, setSearchParams]
  )

  const handleToggleChange = useCallback(
    (value: string): void => {
      if (!value || value === activeTab) return
      if (tabOrder.includes(value as ProductsTabKey)) {
        setActiveTab(value as ProductsTabKey)
      }
    },
    [activeTab, setActiveTab]
  )

  return (
    <div className="min-h-screen pt-14">
      <PageMeta title={t('meta.title')} description={t('meta.description')} />
      <ScrollReveal blur duration={0.3} animateOnMount>
        <div className="mx-auto max-w-screen-2xl space-y-4 sm:space-y-6 px-4 lg:px-8 py-4 sm:py-8">
          <div className="space-y-3">
            {/* Header layout: stacked on mobile, 2 rows on tablet, 3-col grid on desktop */}
            <div className={
              isBelowDesktop
                ? 'flex flex-col gap-3'
                : 'grid grid-cols-[auto_1fr_auto] items-center gap-4'
            }>
              {/* Row 1: Toggle + Add button (on tablet/mobile) */}
              <div className={isBelowDesktop ? 'flex items-center justify-between gap-3' : 'contents'}>
                <ToggleGroup
                  type="single"
                  value={activeTab}
                  onValueChange={handleToggleChange}
                  className="inline-flex rounded-full border border-border bg-card overflow-hidden"
                >
                  {tabOrder.map(tab => (
                    <ToggleGroupItem
                      key={tab}
                      value={tab}
                      aria-label={t(`tabs.${tab}`)}
                      disabled={tab === 'needsReview' && hasConfirmedNoReviewItems}
                      className={
                        isMobile
                          ? 'h-auto px-3 py-2 text-sm font-semibold min-w-[100px] text-muted-foreground hover:text-foreground data-[state=on]:bg-muted-selector data-[state=on]:text-foreground data-[state=on]:shadow-inner'
                          : 'h-auto px-5 py-3 text-base font-semibold min-w-[160px] text-muted-foreground hover:text-foreground data-[state=on]:bg-muted-selector data-[state=on]:text-foreground data-[state=on]:shadow-inner'
                      }
                    >
                      <span className="inline-flex items-center gap-1.5 sm:gap-2">
                        {tab === 'needsReview' && isMobile
                          ? t('tabs.needsReviewShort')
                          : t(`tabs.${tab}`)}
                      </span>
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {/* Tags + Add button - shown inline on tablet/mobile */}
                {isBelowDesktop && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size={isMobile ? 'icon' : 'default'}
                      aria-label={t('tags.manageButton', 'Tags')}
                      onClick={() => navigate('/dashboard/management/tags')}
                    >
                      <Tag className="size-4" />
                      {!isMobile && t('tags.manageButton', 'Tags')}
                    </Button>
                    {activeTab === 'catalog' && (
                      <FloatingActionPanelRoot>
                        {({ closePanel }) => (
                          <>
                            <FloatingActionPanelTrigger title={t('actions.addProduct')} mode="actions">
                              <Plus className="size-4" />
                              {!isMobile && t('actions.addProduct')}
                            </FloatingActionPanelTrigger>
                            <FloatingActionPanelContent>
                              <div className="space-y-1 p-2">
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/import/baselinker')
                                  }}
                                >
                                  <Plus className="size-4" />
                                  {t('actions.importBaselinker')}
                                </FloatingActionPanelButton>
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/import/shopify')
                                  }}
                                >
                                  <Plus className="size-4" />
                                  {t('actions.importShopify', 'Add from Shopify')}
                                </FloatingActionPanelButton>
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/products/new')
                                  }}
                                >
                                  <Package className="size-4" />
                                  {t('create.actions.submit')}
                                </FloatingActionPanelButton>
                              </div>
                            </FloatingActionPanelContent>
                          </>
                        )}
                      </FloatingActionPanelRoot>
                    )}
                  </div>
                )}
              </div>

              {/* Row 2 / Middle column: Search + pagination */}
              <AnimatePresence mode="wait">
                {activeTab === 'catalog' && (
                  <motion.div
                    key="catalog-search"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className={isBelowDesktop ? 'flex items-center gap-2' : 'flex justify-center items-center gap-2'}
                  >
                    <div className="relative flex-1 lg:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('catalog.searchPlaceholder', 'Search products...')}
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pl-9 w-full lg:w-[320px]"
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full size-8"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0 || catalogQuery.isLoading}
                      >
                        <ChevronLeft className="size-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full size-8"
                        onClick={() => setPage(p => p + 1)}
                        disabled={catalogQuery.data?.last || catalogQuery.isLoading || catalogQuery.isPlaceholderData || !catalogQuery.data?.content?.length || (catalogQuery.data?.totalPages !== undefined && page >= catalogQuery.data.totalPages - 1)}
                      >
                        <ChevronRight className="size-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Add button - shown as 3rd column on desktop only */}
              {!isBelowDesktop && (
                <AnimatePresence mode="wait">
                  {activeTab === 'catalog' && (
                    <motion.div
                      key="catalog-actions"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="flex items-center gap-3"
                    >
                      <Button
                        variant="outline"
                        onClick={() => navigate('/dashboard/management/tags')}
                      >
                        <Tag className="size-4" />
                        {t('tags.manageButton', 'Tags')}
                      </Button>
                      <FloatingActionPanelRoot>
                        {({ closePanel }) => (
                          <>
                            <FloatingActionPanelTrigger title={t('actions.addProduct')} mode="actions">
                              <Plus className="size-4" />
                              {t('actions.addProduct')}
                            </FloatingActionPanelTrigger>
                            <FloatingActionPanelContent>
                              <div className="space-y-1 p-2">
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/import/baselinker')
                                  }}
                                >
                                  <Plus className="size-4" />
                                  {t('actions.importBaselinker')}
                                </FloatingActionPanelButton>
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/import/shopify')
                                  }}
                                >
                                  <Plus className="size-4" />
                                  {t('actions.importShopify', 'Add from Shopify')}
                                </FloatingActionPanelButton>
                                <FloatingActionPanelButton
                                  onClick={() => {
                                    closePanel()
                                    navigate('/dashboard/management/products/new')
                                  }}
                                >
                                  <Package className="size-4" />
                                  {t('create.actions.submit')}
                                </FloatingActionPanelButton>
                              </div>
                            </FloatingActionPanelContent>
                          </>
                        )}
                      </FloatingActionPanelRoot>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

          </div>
        </div>
      </ScrollReveal>
      <ScrollReveal delay={0.1} blur duration={0.3} animateOnMount>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {activeTab === 'catalog' ? (
              <CatalogTab
                products={catalogQuery.data?.content ?? []}
                isLoading={catalogQuery.isLoading}
              />
            ) : (
              <NeedsReviewTab isActive={activeTab === 'needsReview'} />
            )}
          </motion.div>
        </AnimatePresence>
      </ScrollReveal>
    </div>
  )
}

export default ProductsPage
